import { BarChartData, formatValue, UnitOptions } from '@/core';

/**
 * Shape of a single row passed to the bar-gauge renderer after null handling
 * and label cleanup. Separated from the chart-wide `BarChartData` because the
 * renderer only needs the resolved numeric value (nulls map to 0) and a
 * display-safe label.
 */
export type BarGaugeDatasetSource = {
  label: string;
  value: number;
};

/**
 * Minimum bar height (px). Also used as the per-row floor when dividing
 * vertical space across rows.
 */
export const BAR_MIN_WIDTH = 14;

/** Maximum bar height (px). Bars cap here when few rows are present. */
export const BAR_MAX_HEIGHT = 120;

/** Visual gap used to estimate whether the full dataset fits in the panel. */
export const BAR_GAP = 6;

/** Gap between rows inside the virtualized list (px). */
export const ROW_GAP_PX = 4;

/** Top + bottom padding reserved for the virtualized list container (px). */
export const CONTAINER_VERTICAL_PADDING_PX = 8;

/**
 * Labels can contain embedded newlines which wrap unpredictably inside a
 * fixed-height row, so we strip them before rendering.
 */
export const REMOVE_NEW_LINES_REGEX = /[\r\n]+/g;

/**
 * Placeholder shown in place of a numeric readout when a series has no value
 * for the current time range. Matches the "--" convention used elsewhere in
 * the viz-framework (table cells, link editor, etc.).
 */
export const NO_VALUE_PLACEHOLDER = '--';

/**
 * Turns the raw query result data into the three parallel arrays the renderer
 * needs:
 *
 * - `datasetValues`: `{ label, value }` with nulls coalesced to `0` (used for
 *   bar-length math and keyed rendering).
 * - `values`: the original nullable values (used downstream for formatting and
 *   threshold lookups where we need to distinguish "no data" from 0).
 * - `labels`: the newline-stripped labels in the same order.
 *
 * Returned as parallel arrays (rather than a single array of rich objects) so
 * the caller can `useMemo` each projection with independent deps if needed.
 */
export function buildDatasetValues(data: BarChartData[]): {
  datasetValues: BarGaugeDatasetSource[];
  values: Array<BarChartData['value']>;
  labels: Array<BarChartData['label']>;
} {
  const values: Array<BarChartData['value']> = [];
  const labels: Array<BarChartData['label']> = [];
  const datasetValues: BarGaugeDatasetSource[] = [];

  data.forEach((datum) => {
    const value = datum.value;
    const label = datum.label.replace(REMOVE_NEW_LINES_REGEX, '');

    datasetValues.push({ label, value: value ?? 0 });
    values.push(value);
    labels.push(label);
  });

  return { datasetValues, values, labels };
}

/**
 * Returns true when the data would need more vertical space than the panel
 * has at the minimum bar height. Used to skip vertical centering and let the
 * virtualized list own the scroll area instead.
 */
export function hasContentOverflow(rowCount: number, height: number): boolean {
  return rowCount * (BAR_MIN_WIDTH + BAR_GAP) > height;
}

/**
 * Picks the per-row bar height so rows distribute the available vertical space
 * evenly, clamped to [{@link BAR_MIN_WIDTH}, {@link BAR_MAX_HEIGHT}]. When no
 * rows are present we return the minimum so the empty state doesn't collapse.
 */
export function calculateBarHeight(rowCount: number, height: number): number {
  if (!rowCount) return BAR_MIN_WIDTH;

  const gapTotal = Math.max(0, rowCount - 1) * ROW_GAP_PX;
  const available = height - CONTAINER_VERTICAL_PADDING_PX - gapTotal;
  const perRow = available / rowCount;

  return Math.min(BAR_MAX_HEIGHT, Math.max(BAR_MIN_WIDTH, perRow));
}

/**
 * Computes the height of the leading spacer the virtualized list should inject
 * as its "Header". When content fits in the panel this grows to vertically
 * center the rows (matching the original ECharts chart's category-axis
 * distribution). When content overflows, the spacer collapses to the minimum
 * top padding so the scroll area starts flush with row 1.
 */
export function calculateTopSpacer(params: {
  hasOverflow: boolean;
  rowCount: number;
  barHeight: number;
  height: number;
}): number {
  const { hasOverflow, rowCount, barHeight, height } = params;
  if (hasOverflow) return CONTAINER_VERTICAL_PADDING_PX / 2;

  const contentHeight = rowCount * barHeight + Math.max(0, rowCount - 1) * ROW_GAP_PX;
  return Math.max(CONTAINER_VERTICAL_PADDING_PX / 2, (height - contentHeight) / 2);
}

/**
 * Fraction of the track width (as a 0-100 percentage) a bar should fill for a
 * given value. Mirrors the original ECharts x-axis semantics: bounded to
 * `[0, max]`, values above `max` are clamped to 100, null/negative to 0, and
 * a non-positive `max` renders as zero rather than diverging.
 */
export function calculateFillPct(value: number, max: number): number {
  if (max <= 0) return 0;
  return Math.min(100, Math.max(0, (value / max) * 100));
}

/**
 * Formats a series value for display next to its bar. Returns the unit-aware
 * formatted string for real numbers, or {@link NO_VALUE_PLACEHOLDER} for null
 * (no-data) values along with an `isMissing` flag so callers can render the
 * placeholder in a muted color and skip behaviors like truncation tooltips.
 *
 * Replaces the legacy ECharts behavior in `BarGaugeBase`, which rendered the
 * literal string "null" for missing values.
 */
export function formatBarGaugeValue(
  value: BarChartData['value'],
  unit: UnitOptions | undefined
): { text: string; isMissing: boolean } {
  if (value === null) {
    return { text: NO_VALUE_PLACEHOLDER, isMissing: true };
  }
  return { text: formatValue(value, unit), isMissing: false };
}

/**
 * Derives the pixel width of the flex-sized bar container so we can do
 * pixel math against the right-anchored message overlay.
 */
export function calculateBarContainerWidth({
  panelWidth,
  rowHorizontalPadding,
  labelColumnWidth,
  valueColumnWidth,
  stackGap,
}: {
  panelWidth: number;
  rowHorizontalPadding: number;
  labelColumnWidth: number;
  valueColumnWidth: number;
  stackGap: number;
}): number {
  return Math.max(0, panelWidth - rowHorizontalPadding * 2 - labelColumnWidth - valueColumnWidth - stackGap * 2);
}

/**
 * Layout result for the inline threshold-message overlay on a bar row. The
 * caller renders an absolutely-positioned text node anchored to the right of
 * the bar container; this struct tells it how wide to draw it, whether the
 * text fits, and whether it lands on top of the colored fill (vs. the empty
 * canvas track) so the caller can pick a readable color.
 */
export interface MessageOverlayLayout {
  /** Width of the text node in px, capped at the available bar space. */
  width: number;
  /** True when the natural text width exceeded the available space. */
  truncated: boolean;
  /**
   * True when the bar's colored fill extends far enough to sit behind the
   * right-anchored text. Callers use this to choose a contrast color.
   */
  insideFill: boolean;
}

/**
 * Compute the layout of the right-anchored threshold message overlay for a
 * single bar row.
 */
export function calculateMessageOverlay(params: {
  barContainerWidth: number;
  fillPct: number;
  naturalTextWidth: number;
  measuredWidthPadding: number;
  rightPad: number;
}): MessageOverlayLayout {
  const { barContainerWidth, fillPct, naturalTextWidth, measuredWidthPadding, rightPad } = params;

  const availableWidth = Math.max(0, barContainerWidth - rightPad);
  const width = Math.min(availableWidth, Math.ceil(naturalTextWidth) + measuredWidthPadding);
  const truncated = naturalTextWidth + measuredWidthPadding > availableWidth;

  const fillPx = (barContainerWidth * fillPct) / 100;
  // Rightmost glyph of the right-anchored text sits at `barContainerWidth - rightPad`.
  // Only treat the text as "inside the fill" when the fill reaches that point.
  const rightEdge = barContainerWidth - rightPad;
  const insideFill = fillPx >= rightEdge;

  return { width, truncated, insideFill };
}
